import { useRef } from 'react'
import { Printer, X, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { OrderModel } from '@/types/model/Order'
import { cn } from '@/lib/utils'
import { useBusinessProfile } from '@/hooks/features/business/useBusinessProfile'
import { copyText } from '@/utils/copyText'
import { useToast } from '@/hooks/ui'
import { useBusiness } from '@/contexts/BusinessContext'

interface OrderInvoiceProps {
    order: OrderModel.Order
    open: boolean
    onClose: () => void
}

export const OrderInvoice = ({ order, open, onClose }: OrderInvoiceProps) => {
    const { profile } = useBusinessProfile();
    const { success, error } = useToast()
    const { formatCurrency } = useBusiness()

    const invoiceRef = useRef<HTMLDivElement>(null)

    if (!open) return null

    const handlePrint = () => {
        window.print()
    }

    const items = order.items || order.order_items || []

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200 print:p-0">
            <div className="flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-card shadow-dialog print:static print:max-h-none print:w-full print:shadow-none">

                {/* Toolbar */}
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3 print:hidden">
                    <p className="text-sm font-semibold text-ink">Invoice</p>
                    <div className="flex items-center gap-2">
                        <Button variant="secondary" onClick={handlePrint}>
                            <Printer />
                            Print
                        </Button>
                        <Button variant="ghost" size="icon" aria-label="Close invoice" onClick={onClose}>
                            <X />
                        </Button>
                    </div>
                </div>

                {/* Invoice sheet */}
                <div className="flex-1 overflow-y-auto bg-subdued p-4 sm:p-6 print:overflow-visible print:bg-card print:p-0">
                    <div
                        id="invoice-root"
                        ref={invoiceRef}
                        className="mx-auto max-w-3xl space-y-8 rounded-lg border border-border bg-card p-6 text-sm text-text-2 sm:p-10 print:max-w-none print:rounded-none print:border-0 print:p-0"
                    >

                        {/* Header: Logo & Meta Info */}
                        <div className="flex flex-col items-start justify-between gap-6 border-b border-border pb-6 sm:flex-row">
                            <div className="space-y-3">
                                {profile?.logo_id ? (
                                    <img src={profile.logo_id} alt="Logo" className="h-10 w-auto object-contain" />
                                ) : (
                                    <p className="text-heading-lg text-ink">{profile?.name || 'Business name'}</p>
                                )}
                                <div className="space-y-0.5 text-xs">
                                    <p>{profile?.address || 'Street address, city, country'}</p>
                                    <p>{profile?.contact_number || 'Phone number'}</p>
                                    <p>{profile?.email || 'email@business.com'}</p>
                                </div>
                            </div>
                            <div className="space-y-1 text-left sm:text-right">
                                <h1 className="text-heading-xl text-ink">Invoice</h1>
                                <div className="space-y-0.5 text-xs">
                                    <button
                                        type="button"
                                        className="group flex items-center gap-1.5 sm:ml-auto"
                                        onClick={async () => {
                                            try {
                                                await copyText(order.order_ref)
                                                success(`Invoice# ${order.order_ref} copied.`)
                                            } catch (err) {
                                                error('Failed to copy reference.')
                                            }
                                        }}
                                    >
                                        <span className="text-muted-foreground">Invoice</span>
                                        <span className="font-semibold text-ink">{order.order_ref}</span>
                                        <Copy className="size-3 text-muted-foreground group-hover:text-ink print:hidden" />
                                    </button>
                                    <p><span className="mr-2 text-muted-foreground">Issue date</span>{new Date(order.created_at).toLocaleDateString()}</p>
                                    <p><span className="mr-2 text-muted-foreground">Due date</span>On receipt</p>
                                </div>
                            </div>
                        </div>

                        {/* Customer & Payment Grid */}
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                            <div className="space-y-1">
                                <h4 className="text-xs font-medium text-muted-foreground">Bill to</h4>
                                <p className="font-medium text-ink">{order.customer_info?.name}</p>
                                <p>{order.customer_info?.email}</p>
                                <p>{order.customer_info?.phone}</p>
                                <p>{order.delivery_info?.address}</p>
                            </div>
                            <div className="space-y-1">
                                <h4 className="text-xs font-medium text-muted-foreground">Ship to</h4>
                                <p className="font-medium text-ink">{order.delivery_info?.name}</p>
                                <p>{order.delivery_info?.phone}</p>
                                <p>{order.delivery_info?.address}</p>
                            </div>
                            <div className="space-y-1">
                                <h4 className="text-xs font-medium text-muted-foreground">Payment</h4>
                                <p className="font-medium uppercase text-ink">{order.payment_method}</p>
                                <div className="flex items-center gap-2">
                                    <span className={cn('size-1.5 rounded-full', order.payment_status === 'paid' ? 'bg-success' : 'bg-warning')} />
                                    <p className="capitalize">{order.payment_status}</p>
                                </div>
                                <p className="text-xs text-muted-foreground">Ref: {order._id.slice(-8).toUpperCase()}</p>
                            </div>
                        </div>

                        {/* Items */}
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-border-strong text-xs text-muted-foreground">
                                    <th className="py-2 pr-4 font-medium">Item</th>
                                    <th className="py-2 text-center font-medium">Qty</th>
                                    <th className="py-2 text-right font-medium">Price</th>
                                    <th className="py-2 text-right font-medium">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-subtle">
                                {items.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="py-3 pr-4">
                                            <p className="font-medium text-ink">{item.product_info?.name}</p>
                                            {item.product_info?.variation_name && (
                                                <p className="text-xs text-muted-foreground">{item.product_info?.variation_name}</p>
                                            )}
                                        </td>
                                        <td className="py-3 text-center tabular-nums">{item.quantity}</td>
                                        <td className="py-3 text-right tabular-nums">{formatCurrency(item.unit_price ?? 0)}</td>
                                        <td className="py-3 text-right font-medium tabular-nums text-ink">{formatCurrency(item.quantity * item.unit_price)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Summary & Notes */}
                        <div className="flex flex-col items-start justify-between gap-8 border-t border-border pt-6 sm:flex-row">
                            <div className="max-w-xs space-y-1">
                                <h4 className="text-xs font-medium text-muted-foreground">Notes</h4>
                                <p className="text-xs">
                                    This is a system generated invoice. Please retain this for your records. For returns or support, please mention your Invoice# {order.order_ref}.
                                </p>
                            </div>
                            <div className="w-full space-y-2 sm:w-64">
                                <div className="flex justify-between">
                                    <span>Subtotal</span>
                                    <span className="tabular-nums text-ink">{formatCurrency(order.subtotal ?? 0)}</span>
                                </div>
                                {(order.discount > 0 || order.coupon_info?.coupon_discount_amount) && (
                                    <div className="flex justify-between">
                                        <span>Discount</span>
                                        <span className="tabular-nums text-ink">- {formatCurrency((order.discount || 0) + (order.coupon_info?.coupon_discount_amount || 0))}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span>Shipping</span>
                                    <span className="tabular-nums text-ink">{formatCurrency(order.delivery_charge ?? 0)}</span>
                                </div>
                                <div className="flex items-center justify-between border-t border-border pt-2">
                                    <span className="font-semibold text-ink">Total due</span>
                                    <span className="text-heading-md tabular-nums text-ink">{formatCurrency(order.total ?? 0)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Signature Line */}
                        <div className="flex items-end justify-between gap-6 pt-12">
                            <div className="w-40 border-b border-border pb-1.5 text-center sm:w-48">
                                <p className="text-xs text-muted-foreground">Customer signature</p>
                            </div>
                            <div className="space-y-2 text-center">
                                <div className="w-40 border-b border-ink pb-1.5 sm:w-48">
                                    <p className="text-xs text-ink">Authorized by</p>
                                </div>
                                <p className="text-xs font-medium text-ink">{profile?.name || 'IWS Systems'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Print Styles */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    body * { visibility: hidden; }
                    #invoice-root, #invoice-root * { visibility: visible; }
                    #invoice-root {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 40px !important;
                    }
                    .print\\:hidden { display: none !important; }
                }
            ` }} />
        </div>
    )
}
